import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from 'layout';
import './ProductHistoryPage.scss';

// Community icons
import slIcon from 'images/sl-icon.png';
import pteIcon from 'images/pte-icon.png';
import fiIcon from 'images/fi-icon.png';
import cacIcon from 'images/cac-icon.png';
import sidejawnVideo from 'images/swiping-hero-animation.mp4';

// Screenshots
import communityPageScreenshot1 from 'images/community_page.png';
import profilePageScreenshot1 from 'images/profile_page.png';
import postPitchPageScreenshot1 from 'images/post_pitch_page.png';
import communityPageMobileScreenshot1 from 'images/community_page_mobile.PNG';
import communityPageMobileScreenshot2 from 'images/community_page_mobile_2.PNG';
import discoveryPageMobileScreenshot1 from 'images/discovery_page_mobile.PNG';
import profileMobileScreenshot1 from 'images/profile_mobile.png';
import profileMobileScreenshot2 from 'images/profile_mobile_2.png';

// All screenshots for gallery navigation
const allScreenshots = [
    { src: communityPageScreenshot1, title: 'Community Page', type: 'desktop' },
    { src: profilePageScreenshot1, title: 'Profile Page', type: 'desktop' },
    { src: postPitchPageScreenshot1, title: 'Post Pitch Page', type: 'desktop' },
    { src: communityPageMobileScreenshot1, title: 'Community Page', type: 'mobile' },
    { src: communityPageMobileScreenshot2, title: 'Community Page Details', type: 'mobile' },
    { src: discoveryPageMobileScreenshot1, title: 'Discovery Page', type: 'mobile' },
    { src: profileMobileScreenshot1, title: 'Profile Page', type: 'mobile' },
    { src: profileMobileScreenshot2, title: 'Profile Page Details', type: 'mobile' },
];

const ProductHistoryPage = () => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setLightboxOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        document.body.style.overflow = 'auto';
    };

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? allScreenshots.length - 1 : prev - 1));
    }, []);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev === allScreenshots.length - 1 ? 0 : prev + 1));
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!lightboxOpen) return;
            
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') goToPrevious();
            if (e.key === 'ArrowRight') goToNext();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxOpen, goToPrevious, goToNext]);

    return (
        <Layout>
            <div className="product-history">
                <header className="product-history__header">
                    <div className="product-history__image-header">
                        <video className='hero' alt="sidejawn-header-photo" autoPlay loop muted playsInline>
                            <source src={sidejawnVideo} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                    <h1>SideJawn: A Look Back</h1>
                    <p className="product-history__intro">
                        SideJawn was built as a recruitment platform for hustlers and entrepreneurs. 
                        Here's how it used to work.
                    </p>
                </header>

                <section className="product-history__section">
                    <h2>How It Worked</h2>
                    <div className="product-history__content">
                        <p>
                            SideJawn connected passionate individuals with startup projects that matched 
                            their skills and interests. Whether you were a developer, designer, marketer, 
                            or business strategist, you could find projects that needed your expertise.
                        </p>
                        <p>
                            Users could create profiles showcasing their skills, browse active projects, 
                            and apply to join teams. Project founders could post their ventures and 
                            recruit team members based on specific skill requirements.
                        </p>
                    </div>
                </section>

                <section className="product-history__section">
                    <h2>Our Go-to-Market Strategy</h2>
                    <div className="product-history__content">
                        <p>
                            We anchored online connections in person through real-life meetings. Our strategy 
                            was to bridge the gap between digital networking and face-to-face interaction by 
                            integrating with local community events.
                        </p>
                        <div className="product-history__communities">
                        <h3>Communities We Experimented With</h3>
                        <div className="product-history__community-cards">
                            <a href="https://www.meetup.com/coffee-code-philly" target="_blank" rel="noopener noreferrer" className="product-history__community-card">
                                <img src={cacIcon} alt="Code & Coffee Philly" />
                            </a>
                            <a href="https://www.phillystartupleaders.org" target="_blank" rel="noopener noreferrer" className="product-history__community-card">
                                <img src={slIcon} alt="Philly Startup Leaders" />
                            </a>
                            <a href="https://phillytechentrepreneurs.com" target="_blank" rel="noopener noreferrer" className="product-history__community-card">
                                <img src={pteIcon} alt="Philly Tech Entrepreneurs" />
                            </a>
                            <a href="https://fi.co" target="_blank" rel="noopener noreferrer" className="product-history__community-card">
                                <img src={fiIcon} alt="Founder Institute" />
                            </a>
                        </div>
                    </div>
                        <p>
                            Users could see who was attending which community events and swipe through personal 
                            video pitches of attendees—learning who they were, what they were working on, and 
                            viewing their related profile info before ever meeting them.
                        </p>
                        <p>
                            This gave users a warm intro when they'd meet in person. Instead of awkward cold 
                            introductions at networking events, you'd already know someone's background, their 
                            current projects, and what kind of collaborators they were looking for.
                        </p>
                    </div>
                </section>

                <section className="product-history__section">
                    <h2>Desktop Experience</h2>
                    <div className="product-history__screenshots product-history__screenshots--desktop">
                        {allScreenshots.filter(s => s.type === 'desktop').map((screenshot, idx) => (
                            <div 
                                key={idx} 
                                className="product-history__screenshot-item"
                                onClick={() => openLightbox(allScreenshots.findIndex(s => s.src === screenshot.src))}
                            >
                                <span>{screenshot.title}</span>
                                <img src={screenshot.src} alt={screenshot.title} />
                            </div>
                        ))}
                    </div>
                </section>

                <section className="product-history__section">
                    <h2>Mobile Experience</h2>
                    <div className="product-history__screenshots product-history__screenshots--mobile">
                        {allScreenshots.filter(s => s.type === 'mobile').map((screenshot, idx) => (
                            <div 
                                key={idx} 
                                className="product-history__screenshot-item product-history__screenshot-item--mobile"
                                onClick={() => openLightbox(allScreenshots.findIndex(s => s.src === screenshot.src))}
                            >
                                <span>{screenshot.title}</span>
                                <img src={screenshot.src} alt={screenshot.title} />
                            </div>
                        ))}
                    </div>
                </section>

                <section className="product-history__section">
                    <h2>Key Features</h2>
                    <ul className="product-history__features">
                        <li>
                            <strong>User Profiles</strong> - Showcase your skills, experience, and video introductions
                        </li>
                        <li>
                            <strong>Partner Discovery</strong> - Browse, filter, and swipe through profiles matching your interests
                        </li>
                        <li>
                            <strong>Team Building</strong> - Message and connect with founders and join startup teams
                        </li>
                        <li>
                            <strong>Community Events</strong> - Discover local entrepreneurship events and meetups
                        </li>
                    </ul>
                </section>

                <section className="product-history__section product-history__section--failure">
                    <h2>How We Failed</h2>
                    <div className="product-history__content">
                        <p>
                            Like many platforms before us, we ran headfirst into the <strong>cold start problem</strong>.
                        </p>
                        <p>
                            We couldn't figure out how to get enough people onto the platform at the same time. 
                            A networking platform is only valuable when there are people to network with, but 
                            convincing users to join when the community is sparse is an uphill battle.
                        </p>
                        <p>
                            After 3 years of building and iterating—creating a variety of features that people 
                            barely used—we had to face the reality that we hadn't cracked the distribution puzzle. 
                            Great features don't matter if no one is around to use them.
                        </p>
                    </div>
                </section>

                <section className="product-history__section product-history__section--next">
                    <h2>What's Next</h2>
                    <div className="product-history__content">
                        <p>
                            We decided to call it quits on SideJawn and work on something simpler and easier 
                            to iterate on. Sometimes the best lessons come from knowing when to pivot.
                        </p>
                        <p>
                            That journey eventually led us to <a href="https://pokito.shop" target="_blank" rel="noopener noreferrer" className="product-history__link">Pokito</a>—a 
                            fresh start with a new approach. We took all of the learnings from SideJawn and applied them to a new product that was easier to grow.
                        </p>
                    </div>
                </section>

                <footer className="product-history__footer">
                    <div className="product-history__footer-emoji">
                        🙏🏼
                    </div>
                    <p>
                        Thank you to everyone who was part of the SideJawn community.
                        It was a fun journey and we learned a lot. Your hustle and passion inspired this platform.
                    </p>
                </footer>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && (
                <div className="product-history__lightbox" onClick={closeLightbox}>
                    <button 
                        className="product-history__lightbox-close" 
                        onClick={closeLightbox}
                        aria-label="Close lightbox"
                    >
                        ×
                    </button>
                    
                    <button 
                        className="product-history__lightbox-nav product-history__lightbox-nav--prev"
                        onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                    
                    <div 
                        className="product-history__lightbox-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={allScreenshots[currentIndex].src} 
                            alt={allScreenshots[currentIndex].title}
                            className={allScreenshots[currentIndex].type === 'mobile' ? 'mobile' : ''}
                        />
                        <div className="product-history__lightbox-caption">
                            <span className="product-history__lightbox-title">
                                {allScreenshots[currentIndex].title}
                            </span>
                            <span className="product-history__lightbox-type">
                                {allScreenshots[currentIndex].type === 'mobile' ? 'Mobile' : 'Desktop'}
                            </span>
                            <span className="product-history__lightbox-counter">
                                {currentIndex + 1} / {allScreenshots.length}
                            </span>
                        </div>
                    </div>
                    
                    <button 
                        className="product-history__lightbox-nav product-history__lightbox-nav--next"
                        onClick={(e) => { e.stopPropagation(); goToNext(); }}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                </div>
            )}
        </Layout>
    );
};

export default ProductHistoryPage;
